import { cn } from "@productlane/lib/src/cn"
import { highlightCodeBlocksHtml } from "@productlane/lib/src/highlight-code"
import { paragraphsFromLooseHtml } from "@productlane/lib/src/html"
import { AttachmentsList, typographyVariants } from "@productlane/ui"

import type { TimelineEventProps } from "./types"
import { sanitizeMessageHtml } from "@/lib/sanitize-message-html"
import { useThreadContext } from "../../../thread-context"
import { AgentMessageAvatar } from "./base/agent-message-avatar"
import { SlackStyleMessage } from "./base/slack-style-message"
import { AI_CHAT_ACTOR_NAME } from "./improve-reply"
import { ImproveReplyButton } from "./improve-reply-panel"
import { extractMessageMedia } from "./message-media"
import { resolveThreadActorImage } from "./thread-actor-image"
import { isAgentActorName, resolveThreadActorName } from "./thread-actor-name"

// About 75 characters a line, and a gap between paragraphs, since the message
// typography zeroes the py-5 the agent puts on every paragraph after the first.
export const AGENT_MESSAGE_CLASS = "max-w-[34rem] [&>*+p]:!mt-3"

export function ThreadMessageEvent({
	event,
	showAvatar = true,
}: TimelineEventProps) {
	const { thread } = useThreadContext()

	const companyLogoUrl = thread.company?.logoUrl || undefined
	const companyName = thread.company?.name || undefined

	if (event.type !== "threadMessage") {
		return null
	}

	const isFromAi = isAgentActorName(event.threadMessage.actorName)
	const body = extractMessageMedia(
		sanitizeMessageHtml(
			isFromAi
				? paragraphsFromLooseHtml(event.threadMessage.text)
				: event.threadMessage.text,
		),
	)
	const senderName = isFromAi
		? AI_CHAT_ACTOR_NAME
		: resolveThreadActorName(event.threadMessage.actorName, thread.contact)
	const senderImage = isFromAi
		? undefined
		: resolveThreadActorImage(event.threadMessage.actorImage, thread.contact)

	return (
		<SlackStyleMessage
			showAvatar={showAvatar}
			senderName={senderName}
			senderEmail={isFromAi ? undefined : thread.contact?.email}
			senderImage={senderImage}
			timestamp={new Date(event.threadMessage.createdAt)}
			companyName={companyName}
			companyLogoUrl={companyLogoUrl}
			isOutbound={isFromAi}
			avatarContent={isFromAi ? <AgentMessageAvatar /> : undefined}
		>
			{event.threadMessage.title && (
				<div className="mb-2 text-[14px] font-semibold">
					{event.threadMessage.title}
				</div>
			)}
			<div
				dangerouslySetInnerHTML={{ __html: highlightCodeBlocksHtml(body.html) }}
				className={typographyVariants({
					variant: "message",
					className: cn(
						"overflow-auto",
						isFromAi ? AGENT_MESSAGE_CLASS : "min-w-full",
					),
				})}
			/>
			{body.images.length > 0 && (
				<AttachmentsList
					className="mt-2"
					attachments={body.images}
					enableViewer
					showPointer={false}
				/>
			)}
			{body.videoUrls.map((src) => (
				<video
					key={src}
					src={src}
					controls
					playsInline
					preload="metadata"
					className="mt-2 block aspect-video w-full max-w-[360px] rounded-[10px] bg-black object-contain"
				>
					<track kind="captions" />
				</video>
			))}
			{isFromAi && (
				<div className="mt-2">
					<ImproveReplyButton
						threadId={thread.id}
						channel="threadMessage"
						messageId={event.threadMessage.id}
					/>
				</div>
			)}
		</SlackStyleMessage>
	)
}
